import React, { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Container, Row, Col, Button, Badge, Modal } from "react-bootstrap";
import logo from "../assets/images/logo.png";
import "./job-description.scss";

export const JobDescription = () => {
  const { jobId } = useParams();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [jobId]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jobDescriptions = {
    "embedded-firmware-developer": {
      title: "Embedded Firmware Developer - Accutroll Products",
      department: "Engineering",
      location: "Remote • On-site • Hybrid",
      type: "Contract • Contract-to-Hire • Full-time",
      salary: "$85,000 - $125,000",
      overview: "We are seeking an experienced Embedded Firmware Developer to join our team and advance the development of our Accutroll devices. These sophisticated embedded systems for fishing and trolling applications feature real-time networking capabilities and a modern React-based web user interface. You'll work on cutting-edge fishing technology that seamlessly integrates embedded systems with modern web development.",
      responsibilities: [
        "Maintain and enhance the ESP32-S3 firmware codebase for Accutroll devices",
        "Develop and improve the React/TypeScript web interface embedded in firmware",
        "Implement new features for control, networking, and user interaction",
        "Debug and resolve issues across the full stack (embedded firmware to web interface)",
        "Optimize performance for real-time control systems in marine environments",
        "Integrate with marine electronics standards (NMEA 2000) and networking protocols",
        "Implement and maintain BLE, WiFi, HTTP/HTTPS, and WebSocket communications",
        "Develop and maintain PID control systems",
        "Write and maintain comprehensive unit tests for embedded systems",
        "Collaborate on system architecture and design decisions",
        "Document code, APIs, and system interfaces",
        "Work with custom hardware platforms and peripheral integration"
      ],
      requirements: [
        "Experience with ESP32 microcontrollers",
        "Experience with ESP-IDF (Espressif IoT Development Framework)",
        "Strong proficiency in embedded C/C++ development",
        "Experience with FreeRTOS operating systems",
        "Networking protocols experience: TCP/IP, HTTP, WebSockets, WiFi, BLE",
        "Proficiency in React, TypeScript, and modern web development",
        "Git version control workflow experience",
        "Build systems experience: PlatformIO, CMake, or similar embedded tools",
        "Embedded debugging tools: JTAG debugging, serial monitors, logic analyzers",
        "Understanding of PWM, ADC, GPIO, and microcontroller peripherals",
        "Experience with RESTful API design and implementation",
        "Problem-solving skills for complex embedded system integration"
      ],
      preferred: [
        "Knowledge of marine electronics and NMEA 2000 standards",
        "Familiarity with PID control systems and motor control",
        "Protocol Buffers and serialization protocols experience",
        "Understanding of embedded security practices and secure communications",
        "Background in IoT device development and fleet management",
        "Experience with cross-platform development (embedded + web)",
        "Material-UI (MUI) and modern React patterns knowledge",
        "Marine industry knowledge or fishing experience",
        "Continuous Integration and automated testing for embedded systems"
      ],
      benefits: [
        "Competitive salary and equity package",
        "Health, dental, and vision insurance",
        "Flexible work arrangements (remote/hybrid options)",
        "Professional development opportunities",
        "Cutting-edge marine technology projects",
        "Hardware and development tool allowances",
        "Conference attendance and training budget",
        "Collaborative team environment",
        "Boat testing opportunities",
        "401(k) retirement plan"
      ],
      technologies: [
        "ESP32-S3 Microcontroller",
        "ESP-IDF Framework",
        "C/C++ Embedded Programming",
        "FreeRTOS",
        "React 19 + TypeScript",
        "PlatformIO + CMake",
        "WiFi & Bluetooth Low Energy",
        "HTTP/HTTPS & WebSockets",
        "NMEA 2000 Protocol",
        "Protocol Buffers",
        "Material-UI (MUI)",
        "PID Control Systems",
        "JTAG Debugging",
        "Git + CI/CD"
      ]
    },
    "senior-flutter-developer": {
      title: "Senior Flutter Developer - Accutroll Device Management Application",
      department: "Engineering",
      location: "Remote • On-site • Hybrid",
      type: "Contract • Contract-to-Hire • Full-time",
      salary: "$80,000 - $120,000",
      overview: "We're seeking a Senior Flutter Developer to maintain and enhance our sophisticated mobile application designed for managing Accutroll devices. This is a unique opportunity to work on cutting-edge fishing technology, combining mobile app development with IoT device management in the specialized fishing electronics industry.",
      responsibilities: [
        "Maintain and enhance the Accutroll Device Manager Flutter application",
        "Debug complex networking and device connectivity issues",
        "Implement new marine device integrations and protocols",
        "Optimize app performance for real-time marine operations",
        "Maintain compatibility across Android and iOS platforms",
        "Extend device discovery and configuration capabilities using mDNS/Bonjour protocols",
        "Enhance user interface",
        "Write comprehensive unit and integration tests",
        "Validate device compatibility and communication reliability",
        "Collaborate with electronics engineers and hardware teams"
      ],
      requirements: [
        "Expert Flutter & Dart Proficiency: Extensive experience in Flutter development with advanced knowledge of Dart, including BLoC state management and complex asynchronous operations.",
        "IoT Device Management Expertise: Proven skills in managing IoT devices, with experience in WiFi networking, mDNS/Bonjour service discovery, and Bluetooth Low Energy (BLE) for seamless Accutroll device integration.",
        "Cross-Platform Optimization: Strong ability to optimize mobile applications for real-time performance and ensure compatibility across Android and iOS platforms, including native integrations and Drift SQLite ORM usage."
      ],
      preferred: [
        "Marine electronics and NMEA2000 protocol familiarity",
        "Embedded systems communication protocols knowledge",
        "QR code scanning and device provisioning experience",
        "Retrofit-based HTTP client implementation",
        "Dependency injection using GetIt service locator",
        "Real-time systems and embedded device communication experience",
        "Experience with marine environment applications",
        "Background task management and app lifecycle handling",
        "Understanding of marine navigation and engine control systems"
      ],
      benefits: [
        "Competitive salary and equity package",
        "Health, dental, and vision insurance",
        "Flexible remote work arrangements",
        "Professional development opportunities",
        "Work with cutting-edge marine technology",
        "Collaborative team environment",
        "Maritime industry domain knowledge development",
        "Opportunity to impact the marine electronics industry"
      ],
      technologies: [
        "Flutter & Dart",
        "BLoC State Management",
        "Drift SQLite ORM",
        "mDNS/Bonjour",
        "Bluetooth Low Energy (BLE)",
        "WiFi Networking",
        "HTTP/REST APIs",
        "NMEA 2000 Protocol",
        "GetIt Dependency Injection",
        "Android/iOS Native Integration"
      ]
    },
  };

  const job = jobDescriptions[jobId];

  if (!job) {
    return <Navigate to="/opportunities" replace />;
  }

  return (
    <div className="job-description-page">
      <Container>
        <Row className="job-header">
          <Col>
            <Link to="/" className="logo-link">
              <img src={logo} alt="Innotech Marine" width={150} className="mb-3" />
            </Link>
            <div className="breadcrumb">
              <Link to="/opportunities">Opportunities</Link> / {job.title}
            </div>
            <h1>{job.title}</h1>
            <div className="job-meta">
              <Badge bg="primary">{job.department}</Badge>
              <Badge bg="secondary">{job.location}</Badge>
              <Badge bg="info">{job.type}</Badge>
            </div>
          </Col>
        </Row>

        <Row>
          <Col lg={8}>
                        <section className="job-section">
              <h3>Overview</h3>
              <p>{job.overview}</p>
            </section>

            {job.technologies && (
              <section className="job-section">
                <h3>Technologies & Tools</h3>
                <div className="tech-tags">
                  {job.technologies.map((tech, index) => (
                    <Badge key={index} bg="light" text="dark" className="me-2 mb-2 tech-badge">
                      {tech}
                    </Badge>
                  ))}
                </div>
              </section>
            )}

            <section className="job-section">
              <h3>Required Qualifications</h3>
              <ul>
                {job.requirements.map((req, index) => (
                  <li key={index}>{req}</li>
                ))}
              </ul>
            </section>

            <section className="job-section">
              <h3>Preferred Qualifications</h3>
              <ul>
                {job.preferred.map((pref, index) => (
                  <li key={index}>{pref}</li>
                ))}
              </ul>
            </section>

            <section className="job-section">
              <h3>Responsibilities</h3>
              <ul>
                {job.responsibilities.map((resp, index) => (
                  <li key={index}>{resp}</li>
                ))}
              </ul>
            </section>
          </Col>

          <Col lg={4}>
            <div className="apply-sidebar">
              <div className="apply-card">
                <h4>Ready to Apply?</h4>
                <p>Send us your resume and a brief cover letter explaining why you're interested in this role.</p>
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="w-100 mb-3"
                  onClick={() => setShowModal(true)}
                >
                  Apply Now
                </Button>
                <Button as={Link} to="/opportunities" variant="outline-secondary" className="w-100">
                  ← Back to Opportunities
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
        </Modal.Header>
        <Modal.Body>
          <div className="application-instructions">
            <h5>How to Apply</h5>
            <p>To apply for this position, please send an email to:</p>
            <div className="email-address">
              <strong>opportunities@innotechmarine.co</strong>
            </div>
            
            <div className="instructions-list">
              <h6>Required Application Materials</h6>
              <ul>
                <li><strong>Resume (PDF preferred):</strong> Submit your resume to highlight your professional background and technical expertise.</li>
                <li><strong>Personalized Email:</strong> Craft an email sharing why you're passionate about our mission to innovate in precision fishing electronics, and why you believe your skills and experience make you a strong fit for this role. Include specific examples of your relevant qualifications and what inspires you about working on solutions like Accutroll. We warmly welcome applications that include this email, as it helps us connect with candidates who share our vision for advancing fishing technology. Please note that applications without this personalized email will not be considered.</li>
              </ul>
            </div>

            <div className="subject-line">
              <h6>Email Subject Line:</h6>
              <p><code>Application for {job.title}</code></p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            as="a" 
            href={`mailto:opportunities@innotechmarine.co?subject=Application for ${job.title}&body=Hi,%0A%0AI'm interested in the ${job.title} position. Please find my resume attached.%0A%0AThank you for your consideration.`}
            variant="primary" 
            className="me-2"
            target="_blank"
          >
            Open Email Client
          </Button>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
